import * as lodash from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import actions from 'src/actions';
import DialogList from 'src/dialogs';
import TsdiClient from 'src/models/AP/Client';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import * as Constants from 'src/components/common/Constants';
import { ODataOptions, ODataFilter, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';
import './DepositReconciliationPage.module.scss';
import PagedResultSet from 'src/models/common/PagedResultSet';
import ClientDeposit from './models/clientdeposit';
import ClientDepositInvoice from './models/clientDepositInvoice';
import ClientDepositContainer from './models/clientdepositcontainer';
import ClientCheck from './models/clientcheck';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import FindClientDialog from  'src/components/client-project/dialogs/FindClientDialog';
import ReportOptionsDialog from 'src/components/accounts-receivable/dialogs/ReportOptionsDialog';
import AddClientDepositCheckDialog from './dialogs/AddClientDepositCheckDialog';
import WarningDialog from './dialogs/WarningDialog';
import InfoDialog from './dialogs/InfoDialog';
import ApplyOverpaymentDialog from './dialogs/ApplyOverpaymentDialog';
import ReconDialog from './dialogs/ReconDialog';
import ArReconTransLine from './models/arReconTransLine';
import DepositAccount from './models/depositAccount';
import DepositDoc from './models/depositDoc';
import DepositDocumentManagementDialog from './dialogs/DepositDocumentManagementDialog';

// icons
import { BiLeftArrowAlt, BiRightArrowAlt, BiArrowToLeft, BiArrowToRight } from 'react-icons/bi';
import {
    FaMoneyCheck, FaLock, FaLockOpen, FaRegSave, FaChevronRight, FaChevronLeft, FaAngleDoubleRight, FaAngleDoubleLeft,
    FaBox, FaUserPlus, FaTrash, FaFileInvoiceDollar, FaEdit, FaCheck, FaSortAlphaDown, FaSortAlphaUp, FaFolderOpen
} from 'react-icons/fa';
import { BsLightningFill } from 'react-icons/bs';
import { VscGraph } from 'react-icons/vsc';
import { ITableConfig } from 'src/components/common/table';
import ARNavBar from './ARNavBar';
import { AnyObject } from 'final-form';
import DepositReconciliation from './models/depositReconciliation';
import { useArServiceCtx } from './AccountsReceivableServiceContext';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';

const FINALIZED_DEPOSIT_DISABLED_TITLE = 'Cannot modify Finalized deposit';

const formatDateForDisplay = (value: string | Date): string => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return '';
    }
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const datePart = `${month}/${day}/${date.getFullYear()}`;
    const hasMeaningfulTime = date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0;
    if (!hasMeaningfulTime) {
        return datePart;
    }
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${datePart} ${hours}:${minutes}`;
};

const formatCurrencyString = (value: number) =>
    value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

const DepositReconciliationPage = () => {
    const dispatch = useDispatch();
    const { register, reset, setValue, getValues, handleSubmit, formState } = useForm();
    const { register: registerChecks, reset: resetChecks } = useForm();
    const { register: registerDepositInvoices, reset: resetDepositInvoices } = useForm();
    const { register: registerReconciliations, reset: resetReconciliations } = useForm();
    const { reset: resetTotals, register: registerTotals, setValue: setTotalsValue, watch: watchTotals } = useForm();
    const { isDirty } = formState;
    const [clients, setClients] = useState<Array<TsdiClient>>(undefined);
    const [clientSearchTerm, setClientSearchTerm] = useState<string>('');
    const [finalizedBy, setFinalizedBy] = useState<string>('');
    const [finalizedDate, setFinalizedDate] = useState<string>('');
    const [selectedClient, setSelectedClient] = useState<TsdiClient>(undefined);
    const [clientSearchOptions, setClientSearchOptions] = useState<ODataOptions>(undefined);
    const [fetchNextClientsPage, setFetchNextClientsPage] = useState<boolean>(true);
    const [clientDeposits, setClientDeposits] = useState<Array<ClientDepositContainer>>([]);
    const [activeDeposits, setActiveDeposits] = useState<Array<ClientDeposit>>([]);
    const [clientChecks, setClientChecks] = useState<Array<ClientCheck>>([]);
    const [clientDepositInvoices, setClientDepositInvoices] = useState<Array<ClientDepositInvoice>>([]);
    const [reconciliations, setReconciliations] = useState<Array<DepositReconciliation>>([]);
    const [loadingReconciliations, setLoadingReconciliations] = useState<boolean>(false);
    const [loadingDepositInvoices, setLoadingDepositInvoices] = useState<boolean>(false);
    const [currentDepositIndex, setCurrentDepositIndex] = useState<number>(0);
    const [selectedDeposit, setSelectedDeposit] = useState<ClientDeposit | null>(null);
    const [selectedInvoiceIndex, setSelectedInvoiceIndex] = useState<number>(undefined);
    const [selectedReconciliationIndex, setSelectedReconciliationIndex] = useState<number>(undefined);
    const [selectedCheckIndex, setSelectedCheckIndex] = useState<number>(undefined);
    const [checkTotalRaw, setCheckTotalRaw] = useState<number>(0);
    const [reconTotalRaw, setReconTotalRaw] = useState<number>(0);
    const [editingCheck, setEditingCheck] = useState<ClientCheck | null>(null);
    const [checkToDelete, setCheckToDelete] = useState<ClientCheck | null>(null);
    const [clientDepositToDelete, setClientDepositToDelete] = useState<ClientDeposit | null>(null);
    const [depositAccounts, setDepositAccounts] = useState<Array<DepositAccount>>([]);
    const [depositDocs, setDepositDocs] = useState<Array<DepositDoc>>([]);
    const [managedDepositForDocs, setManagedDepositForDocs] = useState<ClientDeposit | null>(null);
    const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState<boolean>(false);
    const [pendingNavigationAction, setPendingNavigationAction] = useState<{ action: () => void } | null>(null);
    const [depositToFinalize, setDepositToFinalize] = useState<ClientDeposit | null>(null);
    const [pendingTotalsMismatch, setPendingTotalsMismatch] = useState<boolean>(false);
    const [postResultHeading, setPostResultHeading] = useState<string>('');
    const [postResultMessage, setPostResultMessage] = useState<string>('');
    const [invoiceForOverpayment, setInvoiceForOverpayment] = useState<DepositReconciliation | null>(null);
    const [reconOpSelection, setReconOpSelection] = useState<{ entryNumber: number; description: string; referenceNumber: string; adebit: number; adebitInc: number } | null>(null);
    const [checkForRecon, setCheckForRecon] = useState<ClientCheck | null>(null);
    const [arReconTransLines, setArReconTransLines] = useState<Array<ArReconTransLine>>([]);
    const [outstandingSortField, setOutstandingSortField] = useState<string | undefined>(undefined);
    const [outstandingSortDescending, setOutstandingSortDescending] = useState<boolean>(false);
    const [reconciledSortField, setReconciledSortField] = useState<string | undefined>(undefined);
    const [reconciledSortDescending, setReconciledSortDescending] = useState<boolean>(false);
    const [reconciledPaidFilter, setReconciledPaidFilter] = useState<string>('all');
    const [depositNumberInput, setDepositNumberInput] = useState<string>('');
    const [depositNumberError, setDepositNumberError] = useState<string>('');
    const clientDepositsRef = useRef(clientDeposits);
    const currentDepositIndexRef = useRef(currentDepositIndex);
    const isDirtyRef = useRef(false);
    const appContext = React.useContext<IAppContextState>(AppContext);
    const {
        getClients,
        getClientDeposits,
        getClientDepositChecks,
        getClientDepositInvoices,
        searchClientDepositInvoices,
        getClientReconciledInvoices,
        searchReconciledInvoices,
        insertClientDeposit,
        addClientToDeposit,
        getDepositAccounts,
        updateClientDepositAccounts,
        getDepositDocs,
        addDepositDoc,
        deleteDepositDoc,
        downloadDepositDocBlob,
        downloadDepositDocFromS3,
        insertClientDepositCheck,
        deleteClientDeposit,
        moveInvoiceToReconciled,
        moveInvoiceToOutstanding,
        moveAllInvoicesToOutstanding,
        moveAllInvoicesToReconciled,
        getDepositReconciliationTotals,
        getArReconTransLines,
        postReconTransaction,
        deleteClientDepositCheck,
        updateClientDepositCheck,
        getDepositFinalizeEligibility,
        finalizeDeposit,
        postDeposit
    } = useArServiceCtx();
    const totalsDifference: string = watchTotals('totalsDifference');

    // Initial render
    useEffect(() => {
        onCreate();

        return () => {
            onDestroy();
        };
    }, []);

    useEffect(() => {
        if (selectedClient) {
            // Capture and immediately clear the trigger so this can't possibly fire a second time
            // for the same selection (e.g. on some unrelated re-render) and double-insert the client.
            const client = selectedClient;
            setSelectedClient(undefined);

            const depositDate = getValues().depositDate;
            // Read the CURRENT position via refs (kept fresh by the sync effect below) rather than
            // the closure's clientDeposits/currentDepositIndex, so this can't act on a stale position
            // if the user has navigated between opening the dialog and picking a client.
            const currentGroup = clientDepositsRef.current?.[currentDepositIndexRef.current];
            // A group with no real deposits yet is the local-only placeholder clearForNewDeposit
            // pushes for "New Deposit" - that one needs a freshly server-computed DEP_SEQ. A group
            // that already has at least one deposit is an existing sequence the user has open, and
            // adding a client should join THAT sequence rather than starting a new one.
            const isBrandNewDeposit = !currentGroup?.deposits || currentGroup.deposits.length === 0;

            if (isBrandNewDeposit) {
                const clientDepositPayload = {
                    clientNumber: client.clientNumber, depositDate: depositDate, ExpenseAccount: '1', LeaseHoldAccount: 3
                };
                insertClientDeposit(clientDepositPayload).then((result: number) => {
                    if (result > 0) {
                        getClientDeposits(depositDate).then((innerResult) => {
                            updateClientDeposits(innerResult, true);
                        });
                    }
                });
            } else {
                const currentSequenceNumber = currentGroup.sequenceNumber;
                const clientDepositPayload = {
                    clientNumber: client.clientNumber, depositDate: depositDate, SequenceNumber: currentSequenceNumber, ExpenseAccount: '1', LeaseHoldAccount: 3
                };
                addClientToDeposit(clientDepositPayload).then((result: number) => {
                    if (result > 0) {
                        getClientDeposits(depositDate).then((innerResult) => {
                            addNewClientToCurrentDeposit(innerResult, currentSequenceNumber, client.clientNumber);
                        });
                    }
                });
            }
        }
    }, [selectedClient]);

    useEffect(() => {
        if ((!!clientSearchTerm || clientSearchTerm === '') && !!clientSearchOptions) {
            resetClientSearchOptions();
        }
    }, [clientSearchTerm]);

    useEffect(() => {
        getClients(clientSearchOptions).then((result: Array<TsdiClient>) => {
            setClients(result);
        });
    }, [clientSearchOptions]);

    useEffect(() => {
        if (Array.isArray(clientChecks)) {
            const total = clientChecks.reduce((sum, check) => sum + (check.amount ?? 0), 0);
            if (!isNaN(total)) {
                setCheckTotalRaw(total);
                setTotalsValue('checkTotal', formatCurrency(total));
            }
        }
    }, [clientChecks]);

    useEffect(() => {
        setTotalsValue('totalsDifference', formatCurrency(Number((checkTotalRaw - reconTotalRaw).toFixed(2))));
    }, [checkTotalRaw, reconTotalRaw]);

    useEffect(() => {
        setFinalizedDate('');
        setFinalizedBy('');

        if (!selectedDeposit) {
            return;
        }

        refreshDepositTotals(selectedDeposit.clientNumber, selectedDeposit.depositNumber);

        getClientDepositChecks(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((result: Array<ClientCheck>) => {
            setClientChecks(result);
            resetChecks({ checks: result });
            setSelectedCheckIndex(Array.isArray(result) && result.length > 0 ? 0 : undefined);
        });

        if (selectedDeposit.finalizedDate) {
            setFinalizedDate(new Date(selectedDeposit.finalizedDate).toLocaleString());
        }
        if (selectedDeposit.finalizedBy) {
            setFinalizedBy(selectedDeposit.finalizedBy);
        }

        setLoadingDepositInvoices(true);
        getClientDepositInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((result: Array<ClientDepositInvoice>) => {
            try {
                setClientDepositInvoices(result);
                resetDepositInvoices({ invoices: result });
                setSelectedInvoiceIndex(Array.isArray(result) && result.length > 0 ? 0 : undefined);
            } finally {
                setLoadingDepositInvoices(false);
            }
        });

        setLoadingReconciliations(true);
        getClientReconciledInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((result: Array<DepositReconciliation>) => {
            try {
                setReconciliations(result);
                resetReconciliations({ invoices: result });
                setSelectedReconciliationIndex(Array.isArray(result) && result.length > 0 ? 0 : undefined);
            } finally {
                setLoadingReconciliations(false);
            }
        });
    }, [selectedDeposit]);

    useEffect(() => {
        const depositGroup = clientDeposits?.[currentDepositIndex];

        if (!depositGroup?.deposits) {
            setActiveDeposits([]);
            return;
        }

        setActiveDeposits([...depositGroup.deposits]);
    }, [clientDeposits, currentDepositIndex]);

    useEffect(() => {
        if (Array.isArray(activeDeposits) && activeDeposits.length > 0) {
            reset({
                deposits: activeDeposits
            });
        } else {
            reset({
                deposits: []
            });
        }
    }, [activeDeposits]);

    const initializeClientSearchSettings = () => {
        const tableConfig: ITableConfig = {
            rowCount: 50,
            idField: 'VCHR_NO',
            pageNumber: 1,
            columns: []
        };
        setClientSearchOptions(ODataOptions.from(tableConfig));
    };

    const resetClientSearchOptions = () => {
        setFetchNextClientsPage(true);
        console.log(fetchNextClientsPage);
        let newClientSearchOptions = lodash.cloneDeep(clientSearchOptions);
        newClientSearchOptions.clearFilters();
        newClientSearchOptions.addPaging(1, 50);
        if (typeof clientSearchTerm === 'string') {
            const fieldValues = clientSearchTerm.split(',');
            if (fieldValues[0] !== undefined && !isNaN(Number(fieldValues[0]))) {
                newClientSearchOptions.addFilter(new ODataFilter('CL_NO', ODataFilterOperators.Equals, !!clientSearchTerm ? fieldValues[0] : ''));
            }
            if (fieldValues[1] !== undefined) {
                newClientSearchOptions.addFilter(new ODataFilter('NAME1', ODataFilterOperators.Contains, !!clientSearchTerm ? fieldValues[1] : ''));
            }
            if (fieldValues[2] !== undefined) {
                newClientSearchOptions.addFilter(new ODataFilter('NAME2', ODataFilterOperators.Contains, !!clientSearchTerm ? fieldValues[2] : ''));
            }
            if (fieldValues[3] !== undefined) {
                newClientSearchOptions.addFilter(new ODataFilter('CITY', ODataFilterOperators.Contains, !!clientSearchTerm ? fieldValues[3] : ''));
            }
            if (fieldValues[4] !== undefined) {
                newClientSearchOptions.addFilter(new ODataFilter('STATE', ODataFilterOperators.Contains, !!clientSearchTerm ? fieldValues[4] : ''));
            }
            if (fieldValues[5] !== undefined) {
                newClientSearchOptions.addFilter(new ODataFilter('ZIP', ODataFilterOperators.Contains, !!clientSearchTerm ? fieldValues[5] : ''));
            }
            if (fieldValues[6] !== undefined) {
                newClientSearchOptions.addFilter(new ODataFilter('PHONE', ODataFilterOperators.Contains, !!clientSearchTerm ? fieldValues[6] : ''));
            }
        }
        setClientSearchOptions(newClientSearchOptions);
    };

    const onCreate = () => {
        reset({});
        initializeClientSearchSettings();

        // TODO: Delete when done
        referencePlaceHolder();
        getClientDeposits((new Date()).toLocaleDateString()).then((result) => {
            console.log(result);
        });

        getDepositAccounts().then((result: Array<DepositAccount>) => {
            setDepositAccounts(Array.isArray(result) ? result : []);
        });
    };

    const referencePlaceHolder = () => {
        console.log(clientSearchOptions);
        console.log(selectedClient);
        console.log(hideDialog);
        console.log(displayWarning);
        console.log(findClient);
        console.log(setValue);
        console.log(getValues);
        console.log(PagedResultSet);
        console.log(Constants);
        console.log(RHFSelect);
    };

    /**
     * This function will be called when the user leaves the Page
     */
    const onDestroy = () => {
        console.log('VouchersPage onDestroy()');
    };

    const showDialog = (dialog: DialogList) => {
        dispatch(actions.layout.showDialog(dialog));
    };

    const hideDialog = () => {
        dispatch(actions.layout.hideDialog());
    };

    const displayWarning = (index: number) => {
        showDialog(DialogList.WARNING);
    };

    const reportOptions = () => {
        showDialog(DialogList.REPORT_OPTIONS);
    };

    const onSubmit = async (data: AnyObject) => {
        if (!!data) {
            let finalData = finalizeData(data);
            console.log(finalData);
        }
    };

    const finalizeData = (data: AnyObject) => {
        let finalData: AnyObject = {
            ...data,
        };

        return finalData;
    };

    const onSaveChanges = () => {
        if (isDirty) {
            handleOpenSaveChangesDialog();
        }
    };

    const displayClientDepositsTitle = () => {
        const title = 'Client Deposits';

        return title;
    };

    const displayChecksTitle = () => {
        const title = 'Checks';

        return title;
    };

    const findClient = () => {
        showDialog(DialogList.FIND_CLIENT);
    };

    const moveToLastDeposit = () => {
        const index = clientDeposits.reduce(
            (maxIdx, item, idx, arr) =>
                item.sequenceNumber > arr[maxIdx].sequenceNumber ? idx : maxIdx,
            0
        );
        setCurrentDepositIndex(index);
        setSelectedDeposit(clientDeposits[index]?.deposits?.[0] ?? null);
    };

    const moveToNextDeposit = () => {
        const maxIndex = Math.max(...clientDeposits.map(x => x.sequenceNumber));
        if (currentDepositIndex < maxIndex) {
            const nextIndex = currentDepositIndex + 1;
            setCurrentDepositIndex(nextIndex);
            setSelectedDeposit(clientDeposits[nextIndex]?.deposits?.[0] ?? null);
        }
    };

    const moveToPreviousDeposit = () => {
        const previousIndex = currentDepositIndex > 0 ? currentDepositIndex - 1 : currentDepositIndex;
        setCurrentDepositIndex(previousIndex);
        setSelectedDeposit(clientDeposits[previousIndex]?.deposits?.[0] ?? null);
    };

    const moveToFirstDeposit = () => {
        setCurrentDepositIndex(0);
        setSelectedDeposit(clientDeposits[0]?.deposits?.[0] ?? null);
    };

    // Keeps the input, and the refs the debounced validator below reads from, in sync whenever
    // the position changes via the nav buttons (or a fresh deposit list loads) - never touched
    // by the validator itself, so nav clicks can't feed back into it.
    useEffect(() => {
        clientDepositsRef.current = clientDeposits;
        currentDepositIndexRef.current = currentDepositIndex;
        const sequenceNumber = clientDeposits?.[currentDepositIndex]?.sequenceNumber;
        setDepositNumberInput(sequenceNumber !== undefined && sequenceNumber !== null ? sequenceNumber.toString() : '');
        setDepositNumberError('');
    }, [currentDepositIndex, clientDeposits]);

    // Kept fresh for the debounced validator below, which needs the current dirty state at fire
    // time (its own closure is otherwise fixed at creation).
    useEffect(() => {
        isDirtyRef.current = isDirty;
    }, [isDirty]);

    // Imperative and debounced so only actual keystrokes trigger it - reads clientDeposits/
    // currentDepositIndex via refs (rather than depending on an effect) to avoid stale values
    // without re-running on every nav-triggered change to those same values.
    const validateAndJumpToDepositNumber = useRef(lodash.debounce((rawValue: string) => {
        const trimmed = rawValue.trim();

        if (!trimmed) {
            setDepositNumberError('');
            return;
        }

        if (!/^\d+$/.test(trimmed)) {
            setDepositNumberError('Deposit # must be a whole number.');
            return;
        }

        if (trimmed.length > 3) {
            setDepositNumberError('Deposit # must be at most 3 digits.');
            return;
        }

        const enteredNumber = parseInt(trimmed, 10);
        const deposits = clientDepositsRef.current;

        if (!deposits || deposits.length === 0) {
            setDepositNumberError('No deposits are loaded for this client.');
            return;
        }

        const targetIndex = deposits.findIndex(d => d.sequenceNumber === enteredNumber);

        if (targetIndex === -1) {
            setDepositNumberError(`Deposit # ${enteredNumber} does not exist for this client.`);
            return;
        }

        setDepositNumberError('');

        if (targetIndex !== currentDepositIndexRef.current) {
            const jumpToTarget = () => {
                setCurrentDepositIndex(targetIndex);
                setSelectedDeposit(deposits[targetIndex]?.deposits?.[0] ?? null);
            };

            if (isDirtyRef.current) {
                setPendingNavigationAction({ action: jumpToTarget });
                showDialog(DialogList.WARNING);
            } else {
                jumpToTarget();
            }
        }
    }, 800)).current;

    const handleDepositNumberInputChange = (event: React.FormEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setDepositNumberInput(value);
        validateAndJumpToDepositNumber(value);
    };

    const handleOpenDeleteClientDepositDialog = (clientDeposit: ClientDeposit) => {
        setClientDepositToDelete(clientDeposit);
        showDialog(DialogList.WARNING);
    };

    const handleConfirmDeleteClientDeposit = () => {
        if (!clientDepositToDelete) {
            return;
        }

        deleteClientDeposit(clientDepositToDelete.clientNumber, clientDepositToDelete.depositNumber).then((deleteResult: number) => {
            if (deleteResult > 0) {
                const depositDate = getValues().depositDate;
                getClientDeposits(depositDate).then((result: Array<ClientDepositContainer>) => {
                    setClientDeposits(result);

                    const previousIndex = Math.max(0, currentDepositIndex - 1);
                    setCurrentDepositIndex(previousIndex);
                    setSelectedDeposit(result?.[previousIndex]?.deposits?.[0] ?? null);
                });
            }
            setClientDepositToDelete(null);
        });
    };

    const refreshDepositDocs = (clientNumber: number, depositNumber: number) => {
        getDepositDocs(clientNumber, depositNumber).then((result: Array<DepositDoc>) => {
            setDepositDocs(Array.isArray(result) ? result : []);
        });
    };

    const handleOpenDepositDocuments = (clientDeposit: ClientDeposit) => {
        if (!clientDeposit) {
            return;
        }
        setManagedDepositForDocs(clientDeposit);
        refreshDepositDocs(clientDeposit.clientNumber, clientDeposit.depositNumber);
        showDialog(DialogList.MANAGE_DEPOSIT_DOCUMENTS);
    };

    const handleAddDepositDoc = (description: string, file: File) => {
        if (!managedDepositForDocs) {
            return;
        }

        addDepositDoc(managedDepositForDocs.clientNumber, managedDepositForDocs.depositNumber, description, file.name, file).then(() => {
            refreshDepositDocs(managedDepositForDocs.clientNumber, managedDepositForDocs.depositNumber);
            const depositDate = getValues().depositDate;
            getClientDeposits(depositDate).then((result: Array<ClientDepositContainer>) => {
                setClientDeposits(result);
            });
        });
    };

    const handleDeleteDepositDoc = (docId: number) => {
        if (!managedDepositForDocs) {
            return;
        }

        deleteDepositDoc(docId).then(() => {
            refreshDepositDocs(managedDepositForDocs.clientNumber, managedDepositForDocs.depositNumber);
            const depositDate = getValues().depositDate;
            getClientDeposits(depositDate).then((result: Array<ClientDepositContainer>) => {
                setClientDeposits(result);
            });
        });
    };

    const handleDownloadDepositDoc = (doc: DepositDoc) => {
        if (doc.hasBlob) {
            downloadDepositDocBlob(doc.id, doc.originalFileName);
        } else {
            downloadDepositDocFromS3(doc.s3Key, doc.originalFileName);
        }
    };

    const handleMoveInvoiceToReconciled = () => {
        const selectedInvoice = clientDepositInvoices?.[selectedInvoiceIndex];

        if (!selectedInvoice || !selectedDeposit) {
            return;
        }
        if (finalizedDate) {
            window.alert('This deposit has been finalized. It has already been posted.');
            return;
        }
        if (clientChecks?.some(cc => cc.paidNumber)) {
            window.alert('A/R for this check and client have already been reconciled and posted !!!');
            return;
        }

        moveInvoiceToReconciled(selectedDeposit.clientNumber, selectedDeposit.depositNumber, selectedInvoice.entryNumber).then(() => {
            setSelectedInvoiceIndex(undefined);

            setLoadingDepositInvoices(true);
            getClientDepositInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((result: Array<ClientDepositInvoice>) => {
                try {
                    setClientDepositInvoices(result);
                    resetDepositInvoices({ invoices: result });
                } finally {
                    setLoadingDepositInvoices(false);
                }
            });

            setLoadingReconciliations(true);
            getClientReconciledInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((result: Array<DepositReconciliation>) => {
                try {
                    setReconciliations(result);
                    resetReconciliations({ invoices: result });
                } finally {
                    setLoadingReconciliations(false);
                }
            });

            refreshDepositTotals(selectedDeposit.clientNumber, selectedDeposit.depositNumber);
        });
    };

    const runOutstandingSearch = (sortField?: string, sortDescending?: boolean) => {
        if (!selectedDeposit) {
            return;
        }

        const values = getValues();
        const searchOptions = new ODataOptions();

        if (values.outstandingDescription) {
            searchOptions.addFilter(new ODataFilter('description', ODataFilterOperators.Contains, values.outstandingDescription));
        }
        if (values.referenceNumber) {
            searchOptions.addFilter(new ODataFilter('referenceNumber', ODataFilterOperators.Contains, values.referenceNumber));
        }
        if (values.outstandingRemarks) {
            searchOptions.addFilter(new ODataFilter('remarks', ODataFilterOperators.Contains, values.outstandingRemarks));
        }
        if (values.outstandingTransDate) {
            searchOptions.addFilter(new ODataFilter('transactionDate', ODataFilterOperators.Equals, values.outstandingTransDate));
        }
        if (values.outstandingAmount) {
            searchOptions.addFilter(new ODataFilter('amount', ODataFilterOperators.Equals, values.outstandingAmount));
        }

        const effectiveSortField = sortField !== undefined ? sortField : outstandingSortField;
        const effectiveSortDescending = sortDescending !== undefined ? sortDescending : outstandingSortDescending;
        if (effectiveSortField) {
            searchOptions.addOrderBy(effectiveSortField, effectiveSortDescending ? ODataOrderByDirection.Descending : ODataOrderByDirection.Ascending);
        }

        setLoadingDepositInvoices(true);
        searchClientDepositInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber, searchOptions).then((result: Array<ClientDepositInvoice>) => {
            try {
                setClientDepositInvoices(result);
                resetDepositInvoices({ invoices: result });
            } finally {
                setLoadingDepositInvoices(false);
            }
        });
    };

    const handleOutstandingSearch = () => {
        runOutstandingSearch();
    };

    const handleOutstandingSort = (field: string) => {
        const descending = field === outstandingSortField ? !outstandingSortDescending : false;
        setOutstandingSortField(field);
        setOutstandingSortDescending(descending);
        runOutstandingSearch(field, descending);
    };

    const runReconciledSearch = (sortField?: string, sortDescending?: boolean, paidFilterOverride?: string) => {
        if (!selectedDeposit) {
            return;
        }

        const values = getValues();
        const searchOptions = new ODataOptions();

        if (values.reconciledDescription) {
            searchOptions.addFilter(new ODataFilter('description', ODataFilterOperators.Contains, values.reconciledDescription));
        }
        if (values.reconciledReferenceNumber) {
            searchOptions.addFilter(new ODataFilter('referenceNumber', ODataFilterOperators.Contains, values.reconciledReferenceNumber));
        }
        if (values.reconciledRemarks) {
            searchOptions.addFilter(new ODataFilter('remarks', ODataFilterOperators.Contains, values.reconciledRemarks));
        }
        if (values.reconciledTransDate) {
            searchOptions.addFilter(new ODataFilter('transactionDate', ODataFilterOperators.Equals, values.reconciledTransDate));
        }
        if (values.reconciledAmount) {
            searchOptions.addFilter(new ODataFilter('amount', ODataFilterOperators.Equals, values.reconciledAmount));
        }

        const effectivePaidFilter = paidFilterOverride !== undefined ? paidFilterOverride : reconciledPaidFilter;
        if (effectivePaidFilter === 'paid') {
            searchOptions.addFilter(new ODataFilter('paid', ODataFilterOperators.Equals, '1'));
        } else if (effectivePaidFilter === 'unpaid') {
            searchOptions.addFilter(new ODataFilter('paid', ODataFilterOperators.Equals, '0'));
        }

        const effectiveSortField = sortField !== undefined ? sortField : reconciledSortField;
        const effectiveSortDescending = sortDescending !== undefined ? sortDescending : reconciledSortDescending;
        if (effectiveSortField) {
            searchOptions.addOrderBy(effectiveSortField, effectiveSortDescending ? ODataOrderByDirection.Descending : ODataOrderByDirection.Ascending);
        }

        setLoadingReconciliations(true);
        searchReconciledInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber, searchOptions).then((result: Array<DepositReconciliation>) => {
            try {
                setReconciliations(result);
                resetReconciliations({ invoices: result });
            } finally {
                setLoadingReconciliations(false);
            }
        });
    };

    const handleReconciledSearch = () => {
        runReconciledSearch();
    };

    const handleReconciledSort = (field: string) => {
        const descending = field === reconciledSortField ? !reconciledSortDescending : false;
        setReconciledSortField(field);
        setReconciledSortDescending(descending);
        runReconciledSearch(field, descending);
    };

    const handleReconciledPaidFilterChange = (value: string) => {
        setReconciledPaidFilter(value);
        runReconciledSearch(undefined, undefined, value);
    };

    const handleMoveInvoiceToOutstanding = () => {
        const selectedReconciliation = reconciliations?.[selectedReconciliationIndex];

        if (!selectedReconciliation || !selectedDeposit) {
            return;
        }
        if (finalizedDate) {
            window.alert('This deposit has been finalized. It has already been posted.');
            return;
        }
        if (clientChecks?.some(cc => cc.paidNumber)) {
            window.alert('A/R for this check and client have already been reconciled and posted !!!');
            return;
        }

        moveInvoiceToOutstanding(selectedDeposit.clientNumber, selectedReconciliation.entryNumber).then(() => {
            setSelectedReconciliationIndex(undefined);

            setLoadingDepositInvoices(true);
            getClientDepositInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((result: Array<ClientDepositInvoice>) => {
                try {
                    setClientDepositInvoices(result);
                    resetDepositInvoices({ invoices: result });
                } finally {
                    setLoadingDepositInvoices(false);
                }
            });

            setLoadingReconciliations(true);
            getClientReconciledInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((result: Array<DepositReconciliation>) => {
                try {
                    setReconciliations(result);
                    resetReconciliations({ invoices: result });
                } finally {
                    setLoadingReconciliations(false);
                }
            });

            refreshDepositTotals(selectedDeposit.clientNumber, selectedDeposit.depositNumber);
        });
    };

    const handleMoveAllInvoicesToOutstanding = () => {
        if (!selectedDeposit) {
            return;
        }
        if (finalizedDate) {
            window.alert('This deposit has been finalized. It has already been posted.');
            return;
        }
        if (clientChecks?.some(cc => cc.paidNumber)) {
            window.alert('A/R for this check and client have already been reconciled and posted !!!');
            return;
        }

        const entryNumbers = (reconciliations || []).map(r => r.entryNumber);
        moveAllInvoicesToOutstanding(selectedDeposit.clientNumber, selectedDeposit.depositNumber, entryNumbers).then(() => {
            setSelectedReconciliationIndex(undefined);

            setLoadingDepositInvoices(true);
            getClientDepositInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((result: Array<ClientDepositInvoice>) => {
                try {
                    setClientDepositInvoices(result);
                    resetDepositInvoices({ invoices: result });
                } finally {
                    setLoadingDepositInvoices(false);
                }
            });

            setLoadingReconciliations(true);
            getClientReconciledInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((result: Array<DepositReconciliation>) => {
                try {
                    setReconciliations(result);
                    resetReconciliations({ invoices: result });
                } finally {
                    setLoadingReconciliations(false);
                }
            });

            refreshDepositTotals(selectedDeposit.clientNumber, selectedDeposit.depositNumber);
        });
    };

    const handleMoveAllInvoicesToReconciled = () => {
        if (!selectedDeposit) {
            return;
        }
        if (finalizedDate) {
            window.alert('This deposit has been finalized. It has already been posted.');
            return;
        }
        if (clientChecks?.some(cc => cc.paidNumber)) {
            window.alert('A/R for this check and client have already been reconciled and posted !!!');
            return;
        }

        const entryNumbers = (clientDepositInvoices || []).map(ci => ci.entryNumber);
        moveAllInvoicesToReconciled(selectedDeposit.clientNumber, selectedDeposit.depositNumber, entryNumbers).then(() => {
            setSelectedInvoiceIndex(undefined);

            setLoadingDepositInvoices(true);
            getClientDepositInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((result: Array<ClientDepositInvoice>) => {
                try {
                    setClientDepositInvoices(result);
                    resetDepositInvoices({ invoices: result });
                } finally {
                    setLoadingDepositInvoices(false);
                }
            });

            setLoadingReconciliations(true);
            getClientReconciledInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((result: Array<DepositReconciliation>) => {
                try {
                    setReconciliations(result);
                    resetReconciliations({ invoices: result });
                } finally {
                    setLoadingReconciliations(false);
                }
            });

            refreshDepositTotals(selectedDeposit.clientNumber, selectedDeposit.depositNumber);
        });
    };

    const openFindClientDialog = () => {
        showDialog(DialogList.FIND_CLIENT);
    };

    const handleOpenApplyOverpaymentDialog = (invoice: DepositReconciliation) => {
        setInvoiceForOverpayment(invoice);
        showDialog(DialogList.APPLY_OVERPAYMENT);
    };

    const handleApplyOverpayment = (amount: number) => {
        if (invoiceForOverpayment) {
            setReconOpSelection({
                entryNumber: invoiceForOverpayment.entryNumber,
                description: invoiceForOverpayment.description,
                referenceNumber: invoiceForOverpayment.referenceNumber,
                adebit: invoiceForOverpayment.amount,
                adebitInc: amount
            });
        }
        setInvoiceForOverpayment(null);
        showDialog(DialogList.AR_RECON_OVER_UNDER);
    };

    const handleOpenReconDialog = () => {
        if (!selectedDeposit) {
            return;
        }
        getArReconTransLines(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((result: Array<ArReconTransLine>) => {
            setArReconTransLines(result);
            showDialog(DialogList.AR_RECON_OVER_UNDER);
        });
    };

    const handleAddOverpayment = () => {
        hideDialog();
    };

    const handleClearOverpayment = () => {
        setReconOpSelection(null);
    };

    const handleReconOk = (lines: Array<ArReconTransLine>) => {
        if (!selectedDeposit || !checkForRecon) {
            return;
        }

        const body = {
            lines,
            adebitInc: reconOpSelection?.adebitInc,
            adebitEntryNumber: reconOpSelection?.entryNumber,
            adebitReferenceNumber: reconOpSelection?.referenceNumber
        };

        postReconTransaction(selectedDeposit.clientNumber, selectedDeposit.depositNumber, checkForRecon.number, body).then(() => {
            hideDialog();
            setReconOpSelection(null);
            setCheckForRecon(null);

            setPostResultHeading('Deposit Posted');
            setPostResultMessage('Transactions have been posted...');
            showDialog(DialogList.INFO);

            getClientDepositChecks(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((checksResult: Array<ClientCheck>) => {
                setClientChecks(checksResult);
                resetChecks({ checks: checksResult });
            });

            setLoadingDepositInvoices(true);
            getClientDepositInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((invoicesResult: Array<ClientDepositInvoice>) => {
                try {
                    setClientDepositInvoices(invoicesResult);
                    resetDepositInvoices({ invoices: invoicesResult });
                } finally {
                    setLoadingDepositInvoices(false);
                }
            });

            setLoadingReconciliations(true);
            getClientReconciledInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((reconciledResult: Array<DepositReconciliation>) => {
                try {
                    setReconciliations(reconciledResult);
                    resetReconciliations({ invoices: reconciledResult });
                } finally {
                    setLoadingReconciliations(false);
                }
            });

            refreshDepositTotals(selectedDeposit.clientNumber, selectedDeposit.depositNumber);
        });
    };

    const handleOpenNewCheckDialog = () => {
        if (!selectedDeposit) {
            window.alert('A client must be added first before a check can be added.');
            return;
        }
        if (finalizedDate) {
            window.alert('This deposit has been finalized. No new checks can be added.');
            return;
        }
        setEditingCheck(null);
        showDialog(DialogList.ADD_CLIENT_DEPOSIT_CHECK);
    };

    const handleOpenUpdateCheckDialog = (check: ClientCheck) => {
        if (finalizedDate) {
            window.alert('This deposit has been finalized. No checks can be updated.');
            return;
        }
        setEditingCheck(check);
        showDialog(DialogList.ADD_CLIENT_DEPOSIT_CHECK);
    };

    const handleOpenDeleteCheckDialog = (check: ClientCheck) => {
        if (finalizedDate) {
            window.alert('This deposit has been finalized. No checks can be deleted.');
            return;
        }
        setCheckToDelete(check);
        showDialog(DialogList.WARNING);
    };

    const handleConfirmDeleteCheck = () => {
        if (!checkToDelete) {
            return;
        }

        deleteClientDepositCheck(checkToDelete.clientNumber, checkToDelete.number, checkToDelete.depositNumber).then((result: number) => {
            if (result > 0) {
                refreshChecksAndInvoicesAfterCheckChange(checkToDelete.number);
            }
            setCheckToDelete(null);
        });
    };

    const handleOpenFinalizeDepositDialog = () => {
        if (!selectedDeposit) {
            window.alert('No deposit to finalize... ');
            return;
        }

        getDepositFinalizeEligibility(selectedDeposit.depositNumber).then((eligibility: { canFinalize: boolean; message: string }) => {
            if (!eligibility?.canFinalize) {
                setPostResultHeading('Cannot Finalize Deposit');
                setPostResultMessage(eligibility?.message || 'This deposit cannot be finalized.');
                showDialog(DialogList.INFO);
                return;
            }
            setDepositToFinalize(selectedDeposit);
            showDialog(DialogList.WARNING);
        });
    };

    const handleConfirmFinalizeDeposit = () => {
        if (!depositToFinalize) {
            return;
        }

        const finalizedByUser = appContext?.currentUser?.username;

        finalizeDeposit(depositToFinalize.depositNumber, finalizedByUser).then((finalizeResult: number) => {
            if (finalizeResult > 0) {
                setFinalizedDate(new Date().toLocaleString());
                setFinalizedBy(finalizedByUser);

                const depositDate = getValues().depositDate;
                getClientDeposits(depositDate).then((finalizeDepositResult: Array<ClientDepositContainer>) => {
                    setClientDeposits(finalizeDepositResult);
                });
            }
            setDepositToFinalize(null);
        });
    };

    const handleConfirmAddOverUnderPayment = () => {
        setPendingTotalsMismatch(false);
        handleOpenReconDialog();
    };

    const handleCancelAddOverUnderPayment = () => {
        setPendingTotalsMismatch(false);
        setCheckForRecon(null);
    };

    const handlePostDeposit = (check: ClientCheck) => {
        if (!selectedDeposit) {
            return;
        }
        if (finalizedDate) {
            window.alert('This deposit has been finalized. It has already been posted.');
            return;
        }
        if (check?.paidNumber) {
            window.alert('A/R for this check and client have already been reconciled and posted !!!');
            return;
        }

        postDeposit(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((result: { paidNumber: number; statusCode: string; statusMessage: string }) => {
            if (!result) {
                return;
            }

            if (result.statusCode !== 'POSTED') {
                if (result.statusCode === 'TOTALS_MISMATCH') {
                    setCheckForRecon(check);
                    setPendingTotalsMismatch(true);
                    showDialog(DialogList.WARNING);
                    return;
                }
                setPostResultHeading('Cannot Post Deposit');
                setPostResultMessage(result.statusMessage || 'This deposit cannot be posted.');
                showDialog(DialogList.INFO);
                return;
            }

            setPostResultHeading('Deposit Posted');
            setPostResultMessage(result.statusMessage || 'Transactions have been posted...');
            showDialog(DialogList.INFO);

            getClientDepositChecks(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((checksResult: Array<ClientCheck>) => {
                setClientChecks(checksResult);
                resetChecks({ checks: checksResult });
            });

            setLoadingDepositInvoices(true);
            getClientDepositInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((invoicesResult: Array<ClientDepositInvoice>) => {
                try {
                    setClientDepositInvoices(invoicesResult);
                    resetDepositInvoices({ invoices: invoicesResult });
                } finally {
                    setLoadingDepositInvoices(false);
                }
            });

            setLoadingReconciliations(true);
            getClientReconciledInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((reconciledResult: Array<DepositReconciliation>) => {
                try {
                    setReconciliations(reconciledResult);
                    resetReconciliations({ invoices: reconciledResult });
                } finally {
                    setLoadingReconciliations(false);
                }
            });

            refreshDepositTotals(selectedDeposit.clientNumber, selectedDeposit.depositNumber);
        });
    };

    const refreshChecksAndInvoicesAfterCheckChange = (matchCheckNumber: number) => {
        if (!selectedDeposit) {
            return;
        }

        getClientDepositChecks(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((checksResult: Array<ClientCheck>) => {
            setClientChecks(checksResult);
            resetChecks({ checks: checksResult });

            const changedIndex = checksResult?.findIndex(cc => cc.number === matchCheckNumber);
            setSelectedCheckIndex(changedIndex >= 0 ? changedIndex : undefined);
        });

        getClientDepositInvoices(selectedDeposit.clientNumber, selectedDeposit.depositNumber).then((invoicesResult: Array<ClientDepositInvoice>) => {
            setClientDepositInvoices(invoicesResult);
            resetDepositInvoices({ invoices: invoicesResult });
        });

        refreshDepositTotals(selectedDeposit.clientNumber, selectedDeposit.depositNumber);
    };

    const refreshDepositTotals = (clientNumber: number, depositNumber: number) => {
        getDepositReconciliationTotals(clientNumber, depositNumber).then((result: { reconTotal: number; expTotal: number; lhTotal: number; othTotal: number }) => {
            setReconTotalRaw(result?.reconTotal ?? 0);
            setTotalsValue('recTotal', formatCurrency(result?.reconTotal ?? 0));
            setTotalsValue('expenseTotal', formatCurrency(result?.expTotal ?? 0));
            setTotalsValue('leaseholdTotal', formatCurrency(result?.lhTotal ?? 0));
            setTotalsValue('otherTotal', formatCurrency(result?.othTotal ?? 0));
        });
    };

    const handleAddClientDepositCheck = (clientCheck: ClientCheck) => {
        clientCheck.amount = Number(clientCheck.amount);
        clientCheck.number = Number(clientCheck.number);

        if (editingCheck) {
            updateClientDepositCheck(editingCheck.clientNumber, editingCheck.number, editingCheck.depositNumber, clientCheck).then((result: number) => {
                if (result > 0) {
                    refreshChecksAndInvoicesAfterCheckChange(clientCheck.number);
                    setEditingCheck(null);
                    hideDialog();
                }
            });
            return;
        }

        clientCheck.depositNumber = selectedDeposit?.depositNumber;
        clientCheck.depositDate = clientDeposits[currentDepositIndex].depositDate;
        insertClientDepositCheck(clientCheck, selectedDeposit?.clientNumber).then((result: number) => {
            if (result > 0) {
                refreshChecksAndInvoicesAfterCheckChange(clientCheck.number);
                hideDialog();
            }
        });
    };

    const clearForNewDeposit = () => {
        const currentClientDeposits = lodash.cloneDeep(clientDeposits);
        currentClientDeposits.push(new ClientDepositContainer());
        currentClientDeposits[currentClientDeposits.length - 1].sequenceNumber = currentClientDeposits.length;
        setClientDeposits(currentClientDeposits);
        setClientChecks([]);
        setClientDepositInvoices([]);
        setReconciliations([]);
        setCurrentDepositIndex(currentClientDeposits.length - 1);
        setFinalizedDate('');
        setFinalizedBy('');
        setReconTotalRaw(0);
        resetTotals();
        openFindClientDialog();
    };

    const clearAllDepositData = () => {
        setActiveDeposits([]);
        setClientDeposits([]);
        setClientChecks([]);
        setClientDepositInvoices([]);
        setReconciliations([]);
        setReconTotalRaw(0);
        setSelectedDeposit(null);
        setFinalizedDate('');
        setFinalizedBy('');
    };

    const formatCurrency = (value: number) =>
        new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(value);

    const getNextCheckNumber = () => {
        if (!Array.isArray(clientChecks) || clientChecks.length === 0) {
            return 1;
        }
        return Math.max(...clientChecks.map(cc => cc.number)) + 1;
    };

    // Mirrors the Oracle Forms AR_DETAIL POST-QUERY trigger, which flags a row
    // salmon when its transaction date falls after the deposit's own date.
    const isInvoiceAfterDeposit = (invoice: ClientDepositInvoice) => {
        if (!selectedDeposit?.depositDate || !invoice?.transactionDate) {
            return false;
        }
        return new Date(invoice.transactionDate) > new Date(selectedDeposit.depositDate);
    };

    const onCancel = () => {
        dispatch(actions.layout.hideDialog());
    };

    const updateClientDeposits = (result: AnyObject | void, selectNewest: boolean = false) => {
        if (Array.isArray(result) && result.length > 0) {
            setClientDeposits([...result]);

            const targetIndex = selectNewest
                ? result.reduce(
                    (maxIdx, item, idx, arr) =>
                        item.sequenceNumber > arr[maxIdx].sequenceNumber ? idx : maxIdx,
                    0
                )
                : 0;

            if (Array.isArray(result[targetIndex]?.deposits) && result[targetIndex]?.deposits.length > 0) {
                setValue('finalizedDate', new Date(result[targetIndex]?.deposits[0]?.finalizedDate).toLocaleDateString());
                setCurrentDepositIndex(targetIndex);
                setSelectedDeposit(result[targetIndex].deposits[0]);
            }
        } else {
            clearAllDepositData();
        }
    };

    // Unlike updateClientDeposits (used for "New Deposit", which jumps to the newest sequence),
    // adding a client stays on the SAME deposit sequence the user was already viewing and selects
    // the newly-added client's own row within it, so the checks/invoices/reconciled lists reload
    // for that client via the existing selectedDeposit effect.
    const addNewClientToCurrentDeposit = (result: AnyObject | void, sequenceNumber: number, clientNumber: number) => {
        if (!Array.isArray(result) || result.length === 0) {
            clearAllDepositData();
            return;
        }

        setClientDeposits([...result]);

        const groupIndex = result.findIndex((group) => group.sequenceNumber === sequenceNumber);
        const targetGroupIndex = groupIndex !== -1 ? groupIndex : 0;
        const targetGroup = result[targetGroupIndex];
        const newClientDeposit = targetGroup?.deposits?.find((d: ClientDeposit) => d.clientNumber === clientNumber) ?? targetGroup?.deposits?.[0];

        if (newClientDeposit) {
            setValue('finalizedDate', new Date(newClientDeposit.finalizedDate).toLocaleDateString());
            setCurrentDepositIndex(targetGroupIndex);
            setSelectedDeposit(newClientDeposit);
        }
    };

    const getAccountOptions = (type: string) => {
        return depositAccounts
            .filter((da) => da.type === type)
            .map((da) => ({ value: da.id, label: da.name }));
    };

    // Central gate for any action that would discard the client-deposits form's unsaved edits
    // (navigating, adding/deleting a client, etc). If the form is dirty, the action is queued and
    // the shared warning dialog is shown with an Abandon/Cancel choice instead of running it
    // immediately.
    const runGuardedAction = (action: () => void) => {
        if (isDirty) {
            setPendingNavigationAction({ action });
            showDialog(DialogList.WARNING);
        } else {
            action();
        }
    };

    const handleAbandonChanges = () => {
        const pending = pendingNavigationAction;
        setPendingNavigationAction(null);
        // Reverts just the deposits sub-array to its last-saved values, leaving every other field
        // on this shared form (depositDate, the search panels, etc) exactly as the user left it.
        reset({ ...getValues(), deposits: activeDeposits });
        if (pending?.action) {
            pending.action();
        }
    };

    const handleOpenSaveChangesDialog = () => {
        setShowSaveConfirmDialog(true);
        showDialog(DialogList.WARNING);
    };

    const handleConfirmSaveChanges = () => {
        const formValues = getValues();
        const rows = formValues?.deposits || [];

        const updates = activeDeposits.map((cd, index) => {
            const rowValues = rows[index];
            return updateClientDepositAccounts(cd.clientNumber, cd.depositNumber, {
                ExpenseAccountId: Number(rowValues?.expenseAccount),
                LeaseholdAccountId: Number(rowValues?.leaseholdAccount)
            });
        });

        Promise.all(updates).then(() => {
            setShowSaveConfirmDialog(false);
            const depositDate = getValues().depositDate;
            getClientDeposits(depositDate).then((result: Array<ClientDepositContainer>) => {
                setClientDeposits(result);
            });
        });
    };

    return (
        <>
        <div>
            <ARNavBar/>
                <Page className="dlc-maxer-width">
                    <Page.Section width={12}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel>
                                <div style={{ padding: '.5rem' }}>
                                    <div className="form-row" style={{ marginBottom: '0px', alignItems: 'flex-end' }}>
                                        <RHFInputBox
                                            label={'Deposit Date: '}
                                            register={register}
                                            labelOnLeft={true}
                                            name={'depositDate'}
                                            type={'date'}
                                            defaultVal={new Date().toISOString().split('T')[0]}
                                            style={{ width: '100%', whiteSpace: 'nowrap' }}
                                            bodyStyle={{margin: '0rem, 0rem, 0rem, 2rem', width: '10%', whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
                                            onChange={(e) => {
                                                const validYear = /^\d{4}-\d{2}-\d{2}$/.test(e.currentTarget.value) && e.currentTarget.value[0] !== '0';
                                                const validMonth = /^([1-9]\d{3})-(0[1-9]|1[0-2])-\d{2}$/.test(e.currentTarget.value);
                                                if (validYear && validMonth) {
                                                    const formattedDate = e.currentTarget.value.replace(/(\d{4})-(\d{2})-(\d{2})/, (_, y, m, d) => `${m}/${d}/${y}`);
                                                    getClientDeposits(formattedDate).then((result) => {
                                                        updateClientDeposits(result);
                                                    });
                                                }
                                            }}
                                        />
                                        <input
                                            id={'selectDepositNumber'}
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={3}
                                            value={depositNumberInput}
                                            onChange={(e) => handleDepositNumberInputChange(e)}
                                            title="Enter a deposit # to jump to it"
                                            style={{
                                                fontWeight: 'bold',
                                                marginLeft: '4rem',
                                                width: '3rem',
                                                textAlign: 'center',
                                                border: depositNumberError ? '2px solid red' : '1px solid #ccc',
                                                borderRadius: '4px',
                                                outline: 'none'
                                            }}
                                        />
                                        <label style={{marginLeft: '.5rem', marginRight: '.5rem'}}>of</label>
                                        <label style={{fontWeight: 'bold'}}>{clientDeposits?.length ? Math.max(...clientDeposits.map(x => x.sequenceNumber)) : 0}</label>
                                        <Button
                                            style={{ height: '3rem', marginLeft: '1rem', background: 'transparent'}}
                                            text={''}
                                            title={finalizedDate ? 'Finalized deposits cannot be changed.' : 'Add New Client'}
                                            reactIcon={FaUserPlus}
                                            iconHoverColor="blue"
                                            iconStyle={{width: '2rem', height: '2rem', verticalAlign: 'center'}}
                                            disabled={finalizedDate !== ''}
                                            onClick={() => runGuardedAction(() => openFindClientDialog())}
                                        />
                                        <Button
                                            style={{ height: '3rem', marginLeft: '1rem', background: 'transparent'}}
                                            text={''}
                                            title="Move to First Deposit"
                                            reactIcon={BiArrowToLeft}
                                            iconHoverColor="blue"
                                            iconStyle={{width: '2rem', height: '2rem', verticalAlign: 'center'}}
                                            onClick={() => runGuardedAction(() => moveToFirstDeposit())}
                                        />
                                        <Button
                                            style={{ height: '3rem', marginLeft: '1rem', background: 'transparent'}}
                                            text={''}
                                            title="Move to Previous Deposit"
                                            reactIcon={BiLeftArrowAlt}
                                            iconHoverColor="blue"
                                            iconStyle={{width: '2rem', height: '2rem', verticalAlign: 'center'}}
                                            onClick={() => runGuardedAction(() => moveToPreviousDeposit())}
                                        />
                                        <Button
                                            style={{ height: '3rem', marginLeft: '1rem', background: 'transparent'}}
                                            text={''}
                                            title={finalizedDate ? 'Finalized deposits cannot be changed.' : 'Save Changes'}
                                            reactIcon={FaRegSave}
                                            iconHoverColor="blue"
                                            iconStyle={{width: '2rem', height: '2rem', verticalAlign: 'center'}}
                                            disabled={finalizedDate !== ''}
                                            onClick={() => onSaveChanges()}
                                        />
                                        <Button
                                            style={{ height: '3rem', marginLeft: '1rem', background: 'transparent'}}
                                            text={''}
                                            title="Move to Next Deposit"
                                            reactIcon={BiRightArrowAlt}
                                            iconHoverColor="blue"
                                            iconStyle={{width: '2rem', height: '2rem', verticalAlign: 'center'}}
                                            onClick={() => runGuardedAction(() => moveToNextDeposit())}
                                        />
                                        <Button
                                            style={{ height: '3rem', marginLeft: '1rem', background: 'transparent'}}
                                            text={''}
                                            title="Move to Last Deposit"
                                            reactIcon={BiArrowToRight}
                                            iconHoverColor="blue"
                                            iconStyle={{width: '2rem', height: '2rem', verticalAlign: 'center'}}
                                            onClick={() => runGuardedAction(() => moveToLastDeposit())}
                                        />
                                        <Button
                                            style={{ height: '3rem', marginLeft: '1rem', background: 'transparent'}}
                                            text={''}
                                            title="New Deposit"
                                            reactIcon={FaFileInvoiceDollar}
                                            iconHoverColor="blue"
                                            iconStyle={{width: '2rem', height: '2rem', verticalAlign: 'center'}}
                                            onClick={() => runGuardedAction(() => clearForNewDeposit())}
                                        />
                                        <Button
                                            style={{ height: '3rem', marginLeft: '1rem', background: 'transparent'}}
                                            text={''}
                                            title={finalizedDate ? 'Finalized deposits cannot be changed.' : 'New Check'}
                                            reactIcon={FaMoneyCheck}
                                            iconHoverColor="blue"
                                            iconStyle={{width: '2rem', height: '2rem', verticalAlign: 'center'}}
                                            disabled={finalizedDate !== ''}
                                            onClick={() => handleOpenNewCheckDialog()}
                                        />
                                        <Button
                                            style={{ height: '3rem', marginLeft: '1rem', background: 'transparent'}}
                                            text={''}
                                            title="Documents"
                                            reactIcon={FaFolderOpen}
                                            iconHoverColor="blue"
                                            iconStyle={{width: '2rem', height: '2rem', verticalAlign: 'center'}}
                                            disabled={!selectedDeposit}
                                            onClick={() => handleOpenDepositDocuments(selectedDeposit)}
                                        />
                                        <Button
                                            style={{ height: '3rem', marginLeft: '1rem', background: 'transparent'}}
                                            text={''}
                                            title="Reports"
                                            reactIcon={VscGraph}
                                            iconHoverColor="blue"
                                            iconStyle={{width: '2rem', height: '2rem', verticalAlign: 'center', marginTop: '.4rem'}}
                                            onClick={() => reportOptions()}
                                        />                                        <a style={{ display: 'hidden' }} id="a_voucherCheck" />
                                        <Button
                                            className="blue-hover-button"
                                            style={{ height: '3rem', marginLeft: '1rem', background: 'transparent'}}
                                            text={''}
                                            title={finalizedDate ? 'Finalized' : 'Finalize'}
                                            reactIcon={finalizedDate ? FaLock : FaLockOpen}
                                            iconHoverColor="blue"
                                            iconStyle={{fill: !finalizedDate ? 'green' : 'red', width: '2.2rem', height: '2.2rem', verticalAlign: 'center'}}
                                            disabled={finalizedDate !== ''}
                                            onClick={() => runGuardedAction(() => handleOpenFinalizeDepositDialog())}
                                        />
                                        {finalizedDate && finalizedDate.length > 0 &&
                                            <label
                                                style={{
                                                    borderRadius: '5px', padding: '3px', fontSize: '14px', fontWeight: 'bold',
                                                    backgroundColor: 'red', color: '#f8c3c3', whiteSpace: 'nowrap'
                                                }}
                                            >
                                                Finalized on {finalizedDate} by {finalizedBy}
                                            </label>
                                        }
                                        {(!finalizedDate || finalizedDate.length < 1) &&
                                            <label
                                                style={{
                                                    borderRadius: '5px', padding: '3px', fontSize: '14px', fontWeight: 'bold',
                                                    backgroundColor: 'green', color: '#b2fab6', whiteSpace: 'nowrap'
                                                }}
                                            >
                                                OPEN
                                            </label>
                                        }
                                        {/*<RHFInputBox
                                            style={{whiteSpace: 'nowrap'}}
                                            name={'finalizedDate'}
                                            readOnly={true}
                                            register={register}
                                        />*/}
                                        {depositNumberError &&
                                            <label style={{marginLeft: '1rem', color: 'red', fontWeight: 'bold', whiteSpace: 'nowrap'}}>
                                                {depositNumberError}
                                            </label>}
                                    </div>
                                </div>
                                <label id="BillingCodeError" style={{display: 'none', color: 'red'}}>Test</label>
                            </Panel>
                        </Panel>
                    </Page.Section>
                </Page>
                <Page className="dlc-maxer-width">
                    <Page.Section width={10}>
                        <Panel backgroundColor="#D3D3D3">
                            <div style={{display: 'flex', flexDirection: 'row'}}>
                                <div className={'contentPanelHalfWidth'}>
                                    <form id="depositsForm" onSubmit={handleSubmit(onSubmit)}>
                            <Panel panelTitle={displayClientDepositsTitle()}>
                                <div className="table-container">
                                <table style={{ width: '100%', tableLayout: 'fixed' }}>
                                    <thead>
                                    <tr>
                                        <th style={{width: '7%'}}>Number</th>
                                        <th style={{width: '37%'}}>Name</th>
                                        <th style={{width: '10%'}}>Documents</th>
                                        <th style={{width: '19%'}}>Expense Account</th>
                                        <th style={{width: '19%'}}>Leasehold Account</th>
                                        <th style={{width: '7%'}} />
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {activeDeposits && Array.isArray(activeDeposits) && activeDeposits.map((cd, index: number) => (
                                        <tr
                                            onClick={() => setSelectedDeposit(cd)}
                                            key={cd?.depositNumber  + cd?.clientNumber}
                                            style={{backgroundColor: cd.clientNumber === selectedDeposit?.clientNumber ? '#707070' : ''}}
                                        >
                                        <td style={{width: '7%'}}>
                                            <RHFInputBox
                                                name={`deposits.${index}.clientNumber`}
                                                defaultVal={cd.clientNumber.toString()}
                                                register={register}
                                                readOnly={true}
                                                style={{width: '100%', backgroundColor: cd.clientNumber === selectedDeposit?.clientNumber ? '#FFFF66' : ''}}
                                                bodyStyle={{width: '100%'}}
                                            />
                                        </td>
                                        <td style={{width: '36%'}}>
                                            <RHFInputBox
                                                name={`deposits.${index}.clientName`}
                                                defaultVal={cd.clientName}
                                                register={register}
                                                readOnly={true}
                                                style={{width: '100%', backgroundColor: cd.clientNumber === selectedDeposit?.clientNumber ? '#FFFF66' : ''}}
                                                bodyStyle={{width: '100%'}}
                                            />
                                        </td>
                                        <td style={{width: '14%'}}>
                                            <RHFInputBox
                                                name={`deposits.${index}.documents`}
                                                defaultVal={cd.documents.toString()}
                                                register={register}
                                                readOnly={true}
                                                onDoubleClick={() => handleOpenDepositDocuments(cd)}
                                                style={{width: '100%', textAlign: 'center', backgroundColor: cd.clientNumber === selectedDeposit?.clientNumber ? '#FFFF66' : ''}}
                                                bodyStyle={{width: '100%'}}
                                            />
                                        </td>
                                        <td style={{width: '19%'}}>
                                            <RHFSelect
                                                name={`deposits.${index}.expenseAccount`}
                                                defaultVal={cd.expenseAccount}
                                                options={getAccountOptions('E')}
                                                register={register}
                                                disabled={!!cd.finalizedDate}
                                                style={{width: '100%', backgroundColor: cd.clientNumber === selectedDeposit?.clientNumber ? '#FFFF66' : ''}}
                                            />
                                        </td>
                                        <td style={{width: '19%'}}>
                                            <RHFSelect
                                                name={`deposits.${index}.leaseholdAccount`}
                                                defaultVal={cd.leaseholdAccount}
                                                options={getAccountOptions('L')}
                                                register={register}
                                                disabled={!!cd.finalizedDate}
                                                style={{width: '100%', backgroundColor: cd.clientNumber === selectedDeposit?.clientNumber ? '#FFFF66' : ''}}
                                            />
                                        </td>
                                        <td style={{width: '7%'}}>
                                            <Button
                                                style={{ height: '3rem', marginLeft: '1rem', background: 'transparent'}}
                                                text={''}
                                                title={cd.finalizedDate ? 'Finalized deposits cannot be changed.' : 'Delete this client from this deposit'}
                                                reactIcon={FaTrash}
                                                iconHoverColor="blue"
                                                iconStyle={{fill: 'red', width: '2rem', height: '2rem', verticalAlign: 'center'}}
                                                disabled={!!cd.finalizedDate}
                                                onClick={() => runGuardedAction(() => handleOpenDeleteClientDepositDialog(activeDeposits[index]))}
                                            />
                                        </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                                </div>
                            </Panel>
                            
                            </form>
                            </div>
                            <div className={'contentPanelHalfWidth'}>
                                <form id="checksForm" onSubmit={handleSubmit(onSubmit)}>
                            <Panel panelTitle={displayChecksTitle()}>
                                <div className="table-container">
                                <table>
                                    <thead>
                                    <tr>
                                        <th>Number</th>
                                        <th>Date</th>
                                        <th>Amount</th>
                                        <th>Received</th>
                                        <th />
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {clientChecks && clientChecks.map((cc, index: number) => (
                                        <tr
                                            key={cc.number + cc.clientNumber}
                                            onClick={() => setSelectedCheckIndex(index)}
                                            style={{backgroundColor: index === selectedCheckIndex ? '#707070' : ''}}
                                        >
                                        <td>
                                            <RHFInputBox
                                                name={`checks.${index}.number`}
                                                defaultVal={cc.number.toString()}
                                                register={registerChecks}
                                                readOnly={true}
                                                style={{width: '100%', backgroundColor: index === selectedCheckIndex ? '#FFFF66' : ''}}
                                            />
                                        </td>
                                        <td>
                                            <RHFInputBox
                                                name={`checks.${index}.issueDate`}
                                                defaultVal={formatDateForDisplay(cc.issueDate)}
                                                readOnly={true}
                                                style={{width: '100%', backgroundColor: index === selectedCheckIndex ? '#FFFF66' : ''}}
                                            />
                                        </td>
                                        <td>
                                            <RHFInputBox
                                                name={`checks.${index}.amount`}
                                                defaultVal={cc.amount.toString()}
                                                register={registerChecks}
                                                readOnly={true}
                                                style={{width: '100%', textAlign: 'right', backgroundColor: index === selectedCheckIndex ? '#FFFF66' : ''}}
                                            />
                                        </td>
                                        <td>
                                            <RHFInputBox
                                                name={`checks.${index}.receivedDate`}
                                                defaultVal={formatDateForDisplay(cc.receivedDate)}
                                                readOnly={true}
                                                style={{width: '100%', backgroundColor: index === selectedCheckIndex ? '#FFFF66' : ''}}
                                            />
                                        </td>
                                        <td>
                                            <div style={{display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', alignItems: 'center'}}>
                                                <Button
                                                    style={{ height: '3rem', marginLeft: '1rem', background: 'transparent'}}
                                                    text={''}
                                                    title={finalizedDate ? 'Finalized deposits cannot be changed.' : 'Update check'}
                                                    reactIcon={FaEdit}
                                                    iconHoverColor="blue"
                                                    iconStyle={{fill: '#6393C1', width: '2rem', height: '2rem', verticalAlign: 'center'}}
                                                    disabled={finalizedDate !== ''}
                                                    onClick={() => handleOpenUpdateCheckDialog(cc)}
                                                />
                                                <Button
                                                    style={{ height: '3rem', marginLeft: '.5rem', background: 'transparent'}}
                                                    text={''}
                                                    title={finalizedDate ? 'Finalized deposits cannot be changed.' : 'Delete check'}
                                                    reactIcon={FaTrash}
                                                    iconHoverColor="blue"
                                                    iconStyle={{fill: 'red', width: '2rem', height: '2rem', verticalAlign: 'center'}}
                                                    disabled={finalizedDate !== ''}
                                                    onClick={() => handleOpenDeleteCheckDialog(cc)}
                                                />
                                                <Button
                                                    style={{ height: '3rem', marginLeft: '.5rem', background: 'transparent'}}
                                                    text={''}
                                                    title={finalizedDate ? 'Finalized deposits cannot be changed.' : 'Post'}
                                                    reactIcon={FaCheck}
                                                    iconHoverColor="blue"
                                                    iconStyle={{fill: 'green', width: '2rem', height: '2rem', verticalAlign: 'center'}}
                                                    disabled={finalizedDate !== ''}
                                                    onClick={() => handlePostDeposit(cc)}
                                                />
                                            </div>
                                        </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                                </div>
                            </Panel>
                            </form>
                            </div>
                            </div>
                        </Panel>
                    </Page.Section>
                    <Page.Section width={3}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel panelTitle="Totals">
                                <form id="checksForm" onSubmit={handleSubmit(onSubmit)}>
                                <div style={{display: 'flex', flexDirection: 'column', height: '25vh'}}>
                                        <RHFInputBox
                                            labelOnLeft={true}
                                            style={{whiteSpace: 'nowrap', textAlign: 'right'}}
                                            label={'Check Total'}
                                            labelStyle={{marginRight: '13rem', marginLeft: '1rem', whiteSpace: 'nowrap'}}
                                            name={'checkTotal'}
                                            readOnly={true}
                                            register={registerTotals}
                                        />
                                        <RHFInputBox
                                            labelOnLeft={true}
                                            style={{whiteSpace: 'nowrap', textAlign: 'right'}}
                                            label={'Expense'}
                                            labelStyle={{marginRight: '15.1rem', marginLeft: '1rem', whiteSpace: 'nowrap'}}
                                            name={'expenseTotal'}
                                            register={registerTotals}
                                            readOnly={true}
                                        />
                                        <RHFInputBox
                                            labelOnLeft={true}
                                            style={{whiteSpace: 'nowrap', textAlign: 'right'}}
                                            label={'Leasehold'}
                                            labelStyle={{marginRight: '14rem', marginLeft: '1rem', whiteSpace: 'nowrap'}}
                                            name={'leaseholdTotal'}
                                            register={registerTotals}
                                            readOnly={true}
                                        />
                                        <RHFInputBox
                                            labelOnLeft={true}
                                            style={{whiteSpace: 'nowrap', textAlign: 'right'}}
                                            label={'Other'}
                                            labelStyle={{marginRight: '16.6rem', marginLeft: '1rem', whiteSpace: 'nowrap'}}
                                            name={'otherTotal'}
                                            register={registerTotals}
                                            readOnly={true}
                                        />
                                        <RHFInputBox
                                            labelOnLeft={true}
                                            style={{whiteSpace: 'nowrap', textAlign: 'right'}}
                                            label={'Reconciliation Total'}
                                            labelStyle={{marginRight: '8.3rem', marginLeft: '1rem', whiteSpace: 'nowrap'}}
                                            name={'recTotal'}
                                            register={registerTotals}
                                            readOnly={true}
                                        />
                                        <RHFInputBox
                                            labelOnLeft={true}
                                            label={'Difference'}
                                            name={'totalsDifference'}
                                            register={registerTotals}
                                            readOnly={true}
                                            style={{
                                                whiteSpace: 'nowrap',
                                                textAlign: 'right',
                                                backgroundColor: Number(totalsDifference ? totalsDifference.replace('$', '') : '') === 0 ? '#43a047' : '#e57373',
                                                color: Number(totalsDifference ? totalsDifference.replace('$', '') : '') === 0 ? '#ffffff' : '#ffffff'
                                            }}
                                            labelStyle={{
                                                marginRight: '14rem',
                                                marginLeft: '1rem',
                                                whiteSpace: 'nowrap'
                                            }}
                                        />
                                </div>
                                </form>
                            </Panel>
                        </Panel>
                    </Page.Section>
                </Page>
                <Page className="dlc-maxer-width">
                    <Page.Section width={6}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel panelTitle={'Outstanding Invoices'}>
                                    <div style={{display: 'flex', flexDirection: 'row', marginBottom: '.5rem'}}>
                                        <Button
                                            style={{paddingLeft: '0px', height: '3rem', background: 'transparent'}}
                                            text={''}
                                            title="Search"
                                            reactIcon={BsLightningFill}
                                            iconHoverColor="blue"
                                            iconStyle={{marginBottom: '1rem', background: 'white', border: 'solid black 1px', fill: '#f1f115', width: '3.5rem', height: '3.5rem', verticalAlign: 'center'}}
                                            onClick={() => handleOutstandingSearch()}
                                        />
                                        <RHFInputBox
                                            label={'Description'}
                                            name={`outstandingDescription`}
                                            register={register}
                                            style={{width: '100%'}}
                                            bodyStyle={{width: '100%'}}
                                        />
                                        <RHFInputBox
                                            label={'Reference Number'}
                                            labelStyle={{whiteSpace: 'nowrap'}}
                                            name={`referenceNumber`}
                                            register={register}
                                            style={{width: '100%'}}
                                            bodyStyle={{width: '100%'}}
                                        />
                                        <RHFInputBox
                                            label={'Remarks'}
                                            name={`outstandingRemarks`}
                                            register={register}
                                            style={{width: '100%'}}
                                            bodyStyle={{width: '100%'}}
                                        />
                                        <RHFInputBox
                                            label={'Transaction Date'}
                                            labelStyle={{whiteSpace: 'nowrap'}}
                                            name={`outstandingTransDate`}
                                            register={register}
                                            type={'date'}
                                            style={{width: '100%'}}
                                            bodyStyle={{width: '100%'}}
                                            setValue={setValue}
                                            clearable={true}
                                        />
                                        <RHFInputBox
                                            label={'Amount'}
                                            labelStyle={{whiteSpace: 'nowrap'}}
                                            name={`outstandingAmount`}
                                            register={register}
                                            style={{width: '100%'}}
                                            bodyStyle={{width: '100%'}}
                                        />
                                </div>
                                <form id="invoicesForm" onSubmit={handleSubmit(onSubmit)}>
                                <div className="bottom-table-container">
                                <table>
                                    <thead>
                                    <tr>
                                        {[
                                            { label: 'Description', field: 'description' },
                                            { label: 'Reference Number', field: 'referenceNumber' },
                                            { label: 'Remarks', field: 'remarks' },
                                            { label: 'Transaction Date', field: 'transactionDate' },
                                            { label: 'Amount', field: 'amount' },
                                        ].map(({ label, field }) => {
                                            const willSortDescending = field === outstandingSortField ? !outstandingSortDescending : false;
                                            return (
                                                <th key={field} style={field === 'amount' ? {width: '12rem'} : field === 'transactionDate' ? {width: '12rem', whiteSpace: 'nowrap'} : field === 'description' ? {width: '17rem'} : field === 'referenceNumber' ? {width: '12rem', whiteSpace: 'nowrap'} : undefined}>
                                                    {label}
                                                    <Button
                                                        style={{ height: '3rem', marginLeft: '.25rem', background: 'transparent' }}
                                                        text={''}
                                                        title={`Sort ${label} ${willSortDescending ? 'Descending' : 'Ascending'}`}
                                                        reactIcon={willSortDescending ? FaSortAlphaDown : FaSortAlphaUp}
                                                        iconHoverColor="blue"
                                                        iconStyle={{width: '2rem', height: '2rem', verticalAlign: 'middle'}}
                                                        onClick={() => handleOutstandingSort(field)}
                                                    />
                                                </th>
                                            );
                                        })}
                                    </tr>
                                    </thead>

                                    <tbody>
                                        {loadingDepositInvoices && (
                                            <tr>
                                                <td
                                                    colSpan={6}
                                                    style={{
                                                        textAlign: 'center',
                                                        verticalAlign: 'middle',
                                                        height: '100px'
                                                    }}
                                                >
                                                    <LoadingSpinner />
                                                </td>
                                            </tr>
                                        )}
                                    {!loadingDepositInvoices && clientDepositInvoices && clientDepositInvoices.map((ci, index: number) => (
                                        <tr
                                            key={ci.entryNumber + ci.referenceNumber}
                                            onClick={() => setSelectedInvoiceIndex(index)}
                                            style={{backgroundColor: index === selectedInvoiceIndex ? '#707070' : (isInvoiceAfterDeposit(ci) ? '#FA8072' : '')}}
                                        >
                                        <td style={{width: '17rem'}}>
                                            <RHFInputBox
                                                name={`invoices.${index}.description`}
                                                defaultVal={ci?.description}
                                                register={registerDepositInvoices}
                                                readOnly={true}
                                                style={{width: '100%', backgroundColor: index === selectedInvoiceIndex ? '#FFFF66' : (isInvoiceAfterDeposit(ci) ? '#FA8072' : '')}}
                                            />
                                        </td>
                                        <td style={{width: '12rem'}}>
                                            <RHFInputBox
                                                name={`invoices.${index}.referenceNumber`}
                                                defaultVal={ci?.referenceNumber}
                                                register={registerDepositInvoices}
                                                readOnly={true}
                                                style={{width: '100%', backgroundColor: index === selectedInvoiceIndex ? '#FFFF66' : (isInvoiceAfterDeposit(ci) ? '#FA8072' : '')}}
                                            />
                                        </td>
                                        <td>
                                            <RHFInputBox
                                                name={`invoices.${index}.remarks`}
                                                defaultVal={ci?.remarks}
                                                register={registerDepositInvoices}
                                                readOnly={true}
                                                style={{width: '100%', backgroundColor: index === selectedInvoiceIndex ? '#FFFF66' : (isInvoiceAfterDeposit(ci) ? '#FA8072' : '')}}
                                            />
                                        </td>
                                        <td style={{width: '12rem'}}>
                                            <RHFInputBox
                                                name={`invoices.${index}.transactionDate`}
                                                defaultVal={formatDateForDisplay(ci?.transactionDate)}
                                                readOnly={true}
                                                style={{width: '100%', backgroundColor: index === selectedInvoiceIndex ? '#FFFF66' : (isInvoiceAfterDeposit(ci) ? '#FA8072' : '')}}
                                            />
                                        </td>
                                        <td style={{width: '12rem'}}>
                                            <RHFInputBox
                                                name={`invoices.${index}.amount`}
                                                defaultVal={formatCurrencyString(ci?.amount || 0)}
                                                readOnly={true}
                                                style={{width: '100%', textAlign: 'right', backgroundColor: index === selectedInvoiceIndex ? '#FFFF66' : (isInvoiceAfterDeposit(ci) ? '#FA8072' : '')}}
                                            />
                                        </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                                </div>
                                </form>
                            </Panel>
                        </Panel>
                    </Page.Section>
                    <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                        <div style={{marginTop: '2rem', display: 'flex', flexDirection: 'column', alignContent: 'flex-start'}}>
                            <Button
                                style={{paddingTop: '.5rem', border: 'solid 1px #6393C1', marginTop: '1rem', borderRadius: '8px', height: '25px'}}
                                className="blue-hover-button"
                                title={finalizedDate ? FINALIZED_DEPOSIT_DISABLED_TITLE : 'Move to Reconciled'}
                                iconStyle={{fill: '#6393C1'}}
                                reactIcon={FaChevronRight}
                                disabled={!!finalizedDate || selectedInvoiceIndex === undefined}
                                onClick={() => handleMoveInvoiceToReconciled()}
                            />
                            <Button
                                style={{border: 'solid 1px #6393C1', marginTop: '.5rem', borderRadius: '8px', height: '35px'}}
                                className="blue-hover-button"
                                title={finalizedDate ? FINALIZED_DEPOSIT_DISABLED_TITLE : 'Move all to Reconciled'}
                                iconStyle={{fill: '#6393C1'}}
                                reactIcon={FaAngleDoubleRight}
                                disabled={!!finalizedDate}
                                onClick={() => handleMoveAllInvoicesToReconciled()}
                            />
                        </div>
                        <div style={{marginTop: '8rem', display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                            <Button
                                style={{paddingTop: '.5rem', border: 'solid 1px #6393C1', borderRadius: '8px', height: '25px'}}
                                className="blue-hover-button"
                                title={finalizedDate ? FINALIZED_DEPOSIT_DISABLED_TITLE : 'Move All WIP'}
                                iconStyle={{fill: '#c8a165'}}
                                reactIcon={FaBox}
                                disabled={!!finalizedDate}
                            />
                            <Button
                                style={{marginTop: '.5rem', paddingTop: '.5rem', border: 'solid 1px #6393C1', borderRadius: '8px', height: '25px'}}
                                className="blue-hover-button"
                                title={finalizedDate ? FINALIZED_DEPOSIT_DISABLED_TITLE : 'Move All Voided'}
                                iconStyle={{fill: '#a67c52'}}
                                reactIcon={FaBox}
                                disabled={!!finalizedDate}
                            />
                        </div>
                        <div style={{marginTop: '8rem', display: 'flex', flexDirection: 'column', alignContent: 'flex-end'}}>
                            <Button
                                style={{paddingTop: '.5rem', border: 'solid 1px #6393C1', borderRadius: '8px', height: '25px'}}
                                className="blue-hover-button"
                                title={finalizedDate ? FINALIZED_DEPOSIT_DISABLED_TITLE : 'Move to Outstanding'}
                                iconStyle={{fill: '#6393C1'}}
                                reactIcon={FaChevronLeft}
                                disabled={!!finalizedDate || selectedReconciliationIndex === undefined}
                                onClick={() => handleMoveInvoiceToOutstanding()}
                            />
                            <Button
                                style={{border: 'solid 1px #6393C1', marginTop: '.5rem', borderRadius: '8px', height: '35px'}}
                                className="blue-hover-button"
                                title={finalizedDate ? FINALIZED_DEPOSIT_DISABLED_TITLE : 'Move all to Outstanding'}
                                iconStyle={{fill: '#6393C1'}}
                                reactIcon={FaAngleDoubleLeft}
                                disabled={!!finalizedDate}
                                onClick={() => handleMoveAllInvoicesToOutstanding()}
                            />
                        </div>
                    </div>
                    <Page.Section width={6}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel panelTitle={'Reconciled'}>
                                <div style={{display: 'flex', flexDirection: 'row', marginBottom: '.5rem'}}>
                                    <Button
                                        style={{paddingLeft: '0px', height: '3rem', background: 'transparent'}}
                                        text={''}
                                        title="Search"
                                        reactIcon={BsLightningFill}
                                        iconHoverColor="blue"
                                        iconStyle={{marginBottom: '1rem', background: 'white', border: 'solid black 1px', fill: '#f1f115', width: '3.5rem', height: '3.5rem', verticalAlign: 'center'}}
                                        onClick={() => handleReconciledSearch()}
                                    />
                                    <RHFInputBox
                                        label={'Description'}
                                        name={`reconciledDescription`}
                                        register={register}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                    />
                                    <RHFInputBox
                                        label={'Reference Number'}
                                        labelStyle={{whiteSpace: 'nowrap'}}
                                        name={`reconciledReferenceNumber`}
                                        register={register}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                    />
                                    <RHFInputBox
                                        label={'Remarks'}
                                        name={`reconciledRemarks`}
                                        register={register}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                    />
                                    <RHFInputBox
                                        label={'Transaction Date'}
                                        labelStyle={{whiteSpace: 'nowrap'}}
                                        name={`reconciledTransDate`}
                                        register={register}
                                        type={'date'}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        setValue={setValue}
                                        clearable={true}
                                    />
                                    <RHFInputBox
                                        label={'Amount'}
                                        labelStyle={{whiteSpace: 'nowrap'}}
                                        name={`reconciledAmount`}
                                        register={register}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                    />
                                </div>
                                <div className="bottom-table-container">
                                <table>
                                    <thead>
                                    <tr>
                                        {[
                                            { label: 'Description', field: 'description' },
                                            { label: 'Reference Number', field: 'referenceNumber' },
                                            { label: 'Remarks', field: 'remarks' },
                                            { label: 'Transaction Date', field: 'transactionDate' },
                                            { label: 'Amount', field: 'amount' },
                                            { label: 'Paid?', field: 'paid' },
                                        ].map(({ label, field }) => {
                                            if (field === 'paid') {
                                                return (
                                                    <th key={field} style={{width: '5rem', whiteSpace: 'nowrap'}}>
                                                        {label}
                                                        <select
                                                            value={reconciledPaidFilter}
                                                            onChange={(e) => handleReconciledPaidFilterChange(e.currentTarget.value)}
                                                            title="Filter by paid status"
                                                            style={{marginLeft: '.25rem'}}
                                                        >
                                                            <option value="all">*</option>
                                                            <option value="paid">✔</option>
                                                            <option value="unpaid">🚫</option>
                                                        </select>
                                                    </th>
                                                );
                                            }
                                            const willSortDescending = field === reconciledSortField ? !reconciledSortDescending : false;
                                            return (
                                                <th key={field} style={field === 'amount' ? {width: '12rem'} : field === 'transactionDate' ? {width: '12rem', whiteSpace: 'nowrap'} : field === 'description' ? {width: '17rem'} : field === 'referenceNumber' ? {width: '12rem', whiteSpace: 'nowrap'} : undefined}>
                                                    {label}
                                                    <Button
                                                        style={{ height: '3rem', marginLeft: '.25rem', background: 'transparent' }}
                                                        text={''}
                                                        title={`Sort ${label} ${willSortDescending ? 'Descending' : 'Ascending'}`}
                                                        reactIcon={willSortDescending ? FaSortAlphaDown : FaSortAlphaUp}
                                                        iconHoverColor="blue"
                                                        iconStyle={{width: '2rem', height: '2rem', verticalAlign: 'middle'}}
                                                        onClick={() => handleReconciledSort(field)}
                                                    />
                                                </th>
                                            );
                                        })}
                                    </tr>
                                    </thead>

                                    <tbody>
                                        {loadingReconciliations && (
                                            <tr>
                                                <td
                                                    colSpan={6}
                                                    style={{
                                                        textAlign: 'center',
                                                        verticalAlign: 'middle',
                                                        height: '100px'
                                                    }}
                                                >
                                                    <LoadingSpinner />
                                                </td>
                                            </tr>
                                        )}
                                    {!loadingReconciliations && Array.isArray(reconciliations) && reconciliations.map((r, index: number) => (
                                        <tr
                                            key={r.entryNumber + r.referenceNumber}
                                            onClick={() => setSelectedReconciliationIndex(index)}
                                            onDoubleClick={() => handleOpenApplyOverpaymentDialog(r)}
                                            style={{backgroundColor: index === selectedReconciliationIndex ? '#707070' : ''}}
                                        >
                                        <td style={{width: '17rem'}}>
                                            <RHFInputBox
                                                name={'description'}
                                                defaultVal={r?.description}
                                                register={registerReconciliations}
                                                readOnly={true}
                                                style={{width: '100%', backgroundColor: index === selectedReconciliationIndex ? '#FFFF66' : ''}}
                                            />
                                        </td>
                                        <td style={{width: '12rem'}}>
                                            <RHFInputBox
                                                name={'referenceNumber'}
                                                defaultVal={r?.referenceNumber}
                                                register={registerReconciliations}
                                                readOnly={true}
                                                style={{width: '100%', backgroundColor: index === selectedReconciliationIndex ? '#FFFF66' : ''}}
                                            />
                                        </td>
                                        <td>
                                            <RHFInputBox
                                                name={'remarks'}
                                                defaultVal={r?.remarks}
                                                register={registerReconciliations}
                                                readOnly={true}
                                                style={{width: '100%', backgroundColor: index === selectedReconciliationIndex ? '#FFFF66' : ''}}
                                            />
                                        </td>
                                        <td style={{width: '12rem'}}>
                                            <RHFInputBox
                                                name={'transactionDate'}
                                                defaultVal={formatDateForDisplay(r.transactionDate)}
                                                readOnly={true}
                                                style={{width: '100%', backgroundColor: index === selectedReconciliationIndex ? '#FFFF66' : ''}}
                                            />
                                        </td>
                                        <td style={{width: '12rem'}}>
                                            <RHFInputBox
                                                name={'amount'}
                                                defaultVal={formatCurrencyString(r?.amount || 0)}
                                                readOnly={true}
                                                style={{width: '100%', textAlign: 'right', backgroundColor: index === selectedReconciliationIndex ? '#FFFF66' : ''}}
                                            />
                                        </td>
                                        <td style={{width: '4rem'}}>
                                            <RHFInputBox
                                                name={'paid'}
                                                defaultVal={r?.paid ? '✔' : '🚫'}
                                                register={registerReconciliations}
                                                readOnly={true}
                                                style={{
                                                    width: '100%',
                                                    textAlign: 'center',
                                                    color: r?.paid ? '#43a047' : 'red',
                                                    backgroundColor: index === selectedReconciliationIndex ? '#FFFF66' : ''
                                                }}
                                            />
                                        </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                                </div>
                            </Panel>
                        </Panel>
                    </Page.Section>
                </Page>
        </div>
        <FindClientDialog
            dialogName={DialogList.FIND_CLIENT}
            hideDialog={hideDialog}
            clients={clients}
            setClientSearchTerm={setClientSearchTerm}
            onCancel={onCancel}
            style={{maxHeight: '50vw'}}
            setSelectedClient={setSelectedClient}
        />
        <ReportOptionsDialog
            dialogName={DialogList.REPORT_OPTIONS}
            hideDialog={hideDialog}
            showDialog={showDialog}
            reportTypes={[
                { value: 'ARdeposits', label: 'Daily Deposit' },
                { value: 'LHdeposits', label: 'Leasehold Deposit' },
                { value: 'DDdeposits', label: 'Daily (by deposit)' }
            ]}
        />
        <AddClientDepositCheckDialog
            dialogName={DialogList.ADD_CLIENT_DEPOSIT_CHECK}
            client={selectedDeposit ? { clientNumber: selectedDeposit.clientNumber, clientName: selectedDeposit.clientName } as TsdiClient : undefined}
            existingCheck={editingCheck}
            nextCheckNumber={getNextCheckNumber()}
            handleAddCheck={handleAddClientDepositCheck}
            hideDialog={hideDialog}
        />
        <DepositDocumentManagementDialog
            dialogName={DialogList.MANAGE_DEPOSIT_DOCUMENTS}
            clientNumber={managedDepositForDocs?.clientNumber}
            depositNumber={managedDepositForDocs?.depositNumber}
            documentList={depositDocs}
            addDocument={handleAddDepositDoc}
            deleteDocument={handleDeleteDepositDoc}
            downloadDocument={handleDownloadDepositDoc}
            hideDialog={hideDialog}
        />
        <WarningDialog
            dialogName={DialogList.WARNING}
            handleConfirm={
                pendingNavigationAction
                    ? handleAbandonChanges
                    : (showSaveConfirmDialog
                        ? handleConfirmSaveChanges
                        : (checkToDelete
                            ? handleConfirmDeleteCheck
                            : (depositToFinalize
                                ? handleConfirmFinalizeDeposit
                                : (clientDepositToDelete
                                    ? handleConfirmDeleteClientDeposit
                                    : handleConfirmAddOverUnderPayment))))
            }
            handleCancellation={() => {
                setCheckToDelete(null);
                setDepositToFinalize(null);
                setClientDepositToDelete(null);
                setPendingNavigationAction(null);
                setShowSaveConfirmDialog(false);
                handleCancelAddOverUnderPayment();
            }}
            hideDialog={hideDialog}
            confirmLabel={pendingNavigationAction ? 'Abandon' : undefined}
            label={
                pendingNavigationAction
                    ? 'You have unsaved changes to the client deposit accounts. Abandon them and continue?'
                    : (showSaveConfirmDialog
                        ? 'Are you sure you want to save the changes to the client deposit accounts?'
                        : (checkToDelete
                            ? `Are you sure you want to delete check #${checkToDelete.number}?`
                            : (depositToFinalize
                                ? 'Are you sure want to mark this deposit final? Once marked final, no clients or invoices may be added to this deposit.'
                                : (clientDepositToDelete
                                    ? `Are you sure you want to delete ${clientDepositToDelete.clientName}'s checks and remove them from this deposit?`
                                    : (pendingTotalsMismatch
                                        ? 'The checks entered and the transactions marked posted do not match. Do you wish to add a under/over payment?'
                                        : undefined)))))
            }
            message={
                pendingNavigationAction
                    ? 'Click Abandon to continue without saving, or Cancel to go back.'
                    : (showSaveConfirmDialog
                        ? 'Click OK to save, Cancel to go back.'
                        : (depositToFinalize
                            ? 'Press OK to mark final. Press Cancel if not deposit is not ready.'
                            : (pendingTotalsMismatch
                                ? 'Press OK to add an over/under payment. Press Cancel to go back.'
                                : undefined)))
            }
        />
        <InfoDialog
            dialogName={DialogList.INFO}
            hideDialog={hideDialog}
            heading={postResultHeading}
            message={postResultMessage}
        />
        <ApplyOverpaymentDialog
            dialogName={DialogList.APPLY_OVERPAYMENT}
            hideDialog={hideDialog}
            invoice={invoiceForOverpayment}
            onApply={handleApplyOverpayment}
        />
        <ReconDialog
            dialogName={DialogList.AR_RECON_OVER_UNDER}
            hideDialog={hideDialog}
            lines={arReconTransLines}
            opSelection={reconOpSelection}
            onAddOverpayment={handleAddOverpayment}
            onClearOverpayment={handleClearOverpayment}
            onOk={handleReconOk}
        />
        </>
    );
};

export default DepositReconciliationPage;



// WEBPACK FOOTER //
// ./src/components/accounts-receivable/DepositReconciliationPage.tsx